// Journeys that stopped because someone wrote back.
//
// A reply pauses the enrollment rather than exiting it (see the inbound
// webhook): exiting is for bounces, complaints, unsubscribes and claims, and
// means never again. A reply means a person took over, so the enrollment waits
// here until you decide what happens next.
//
// Three ways out, matching the three things you actually want to do after
// reading a reply: pick it back up, send them something written for them, or
// stop for good.

import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import client from '../lib/feathers';

export default function PausedEnrollments({ contactId, onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [delayDays, setDelayDays] = useState(2);
  // Latch on WHICH contact we fetched, not a bare boolean. A bare boolean
  // never resets, so navigating from one contact to another would keep
  // showing the first one's paused journeys. Latching at all (rather than
  // guarding on rows.length) is because an empty result never satisfies a
  // length guard and the effect would refetch on every render.
  const loadedForRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await client.service('admin/outreach-enrollment').find({
        query: { contactId, status: 'paused' },
      });
      setRows(r.data || []);
    } catch (err) {
      toast.error(err.message || 'Could not load paused journeys');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!contactId || loadedForRef.current === contactId) return;
    loadedForRef.current = contactId;
    load();
  }, [contactId]);

  const act = async (id, patch, msg) => {
    try {
      await client.service('admin/outreach-enrollment').patch(id, patch);
      toast.success(msg);
      setComposing(null); setSubject(''); setBody('');
      await load();
      if (onChange) onChange();
    } catch (err) {
      toast.error(err.message || 'That did not work');
    }
  };

  if (loading || !rows.length) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
      <h3 className="text-sm font-semibold text-amber-900">
        {rows.length === 1 ? 'A journey is paused' : `${rows.length} journeys are paused`}
      </h3>
      <p className="text-xs text-amber-800 mt-1 mb-3">
        Nothing further will send automatically until you pick one of these.
      </p>

      {rows.map((e) => (
        <div key={e.id} className="bg-white border border-amber-200 rounded-md p-3 mb-2 last:mb-0">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-gray-800">
              <span className="font-medium">{e.journey?.name || 'Journey'}</span>
              <span className="text-gray-500">
                {' '}at step {(e.currentStepIndex ?? 0) + 1}
                {e.pauseReason ? `, paused because they ${e.pauseReason}` : ''}
              </span>
              {e.injectedSteps?.length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-800">
                  {e.injectedSteps.length} follow-up{e.injectedSteps.length === 1 ? '' : 's'} queued
                </span>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              <button type="button" className="text-xs text-blue-600 hover:underline"
                onClick={() => setComposing(composing === e.id ? null : e.id)}>
                Write a follow-up
              </button>
              <button type="button" className="text-xs text-blue-600 hover:underline"
                onClick={() => act(e.id, { status: 'active' }, 'Journey resumed')}>
                Resume
              </button>
              <button type="button" className="text-xs text-red-600 hover:underline"
                onClick={() => act(e.id, { status: 'exited', exitReason: 'handled by hand' }, 'Journey stopped')}>
                Stop for good
              </button>
            </div>
          </div>

          {composing === e.id && (
            <div className="mt-3 border-t border-gray-100 pt-3 space-y-2">
              <p className="text-xs text-gray-500">
                Queued for this contact only. The journey itself is unchanged, so
                nobody else receives it.
              </p>
              <input
                type="text" value={subject} onChange={(ev) => setSubject(ev.target.value)}
                placeholder="Subject"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
              <textarea
                value={body} onChange={(ev) => setBody(ev.target.value)} rows={5}
                placeholder="Body. {{contactName}} and {{wineryName}} work here."
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
              />
              <div className="flex items-center justify-between">
                <label htmlFor={`delay-${e.id}`} className="text-xs text-gray-600">
                  Send in
                  <input
                    id={`delay-${e.id}`} type="number" min="1" value={delayDays}
                    onChange={(ev) => setDelayDays(ev.target.value)}
                    className="mx-2 w-16 border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                  day(s)
                </label>
                <button
                  type="button"
                  disabled={!subject.trim() || !body.trim()}
                  className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md disabled:opacity-40"
                  onClick={() => act(e.id, {
                    status: 'active',
                    addStep: { subject: subject.trim(), body: body.trim(), delayDays: Number(delayDays) || 1 },
                  }, 'Follow-up queued, journey resumed')}
                >
                  Queue and resume
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
